'use client';

import { c as _c } from "react/compiler-runtime";
import type { ComponentProps } from 'react';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { InterfaceChainUtils, parseApiAsset, toBigInt } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { Avatar, ClipboardButton, NetworkAvatar } from '@morpho-org/ui-v6/client';
import { Tag } from '@morpho-org/ui-v6/server';
import { cn, formatters, getMarketNameParts } from '@morpho-org/ui-v6/utils';
import { Button, Icon, Toggletip, ToggletipContent, ToggletipTrigger, Tooltip, TooltipContent, TooltipTrigger, useSegmentedCopy } from '@repo/ui';
import { Info } from '@repo/ui/icons';
import { abbreviateAddress } from '@repo/utils';
import type { TrustedByCurator } from '@/components/MarketPage/utils/createMarketTrustedByCurators';
import type { MetricTooltipEventProperties } from '@/lib/analytics/client/events';
import { NO_LEND_POINTS } from '@/components/charts/helpers/getPoints';
import { useMarketPoints } from '@/components/charts/hooks/useMarketPoints';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import { InfoHoverCardTrigger } from '@/components/common/InfoHoverCardTrigger';
import RewardsHoverCard from '@/components/common/RewardsHoverCard/RewardsHoverCard';
import { WarningIcon } from '@/components/common/WarningIcon';
import { aggregateMarketWarnings } from '@/helpers/aggregateMarketWarnings';
import { getParsedValue } from '@/helpers/formatters';
import { getNetRateOnMarket } from '@/helpers/getNativeApy';
import { splitValueAndUnit } from '@/helpers/number';
import { normalizeMarketApyRewards, toCollateralRewardEntries } from '@/helpers/rewards';
import { getWarningsColor } from '@/helpers/warnings';
import { trackMetricTooltipOpenChange } from '@/lib/analytics/client/trackers/metric-tooltip';
import type { HeroAsset, MarketHeroData } from './HeroMarket.mapper';
import { MarketLiquidityBreakdownHoverCard } from './MarketLiquidityBreakdownHoverCard';
import { MarketTrustedBy } from './MarketTrustedBy';
export interface HeroMarketSectionClientProps {
  market: MarketHeroData;
  trustedByCurators: TrustedByCurator[];
}
type MarketTitleColor = 'text.interactive.error' | 'text.interactive.warning' | 'text.tertiary';
const marketTitleColorClasses: Record<MarketTitleColor, string> = {
  'text.interactive.error': 'text-destructive-foreground',
  'text.interactive.warning': 'text-warning-foreground',
  'text.tertiary': 'text-muted-foreground'
};

/**
 * Market header title. Renders title-m typography with `word-break: break-all`,
 * applying a text-color override only when an explicit color token is provided
 * (otherwise text-title-md's default text-primary applies — matching v5 getColor).
 */
const MarketTitle = t0 => {
  const $ = _c(5);
  const {
    color,
    children
  } = t0;
  const t1 = color && marketTitleColorClasses[color];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("text-title-md max-sm:text-title-sm break-all", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <span className={t2}>{children}</span>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};

/** `size="big"` (60px) on desktop; on phones the pair shrinks so `◉ LOAN │ ◉ COLLATERAL` fits one row. */
const heroAvatarClassName = 'max-sm:size-[38px]';
type HeroRateStatProps = {
  label: string;
  metric: Extract<MetricTooltipEventProperties, {
    page: 'market';
  }>['metric'];
  formattedNetApy: string;
  hoverCardProps: ComponentProps<typeof RewardsHoverCard>;
};

/** One hero rate readout. Borrow and Lend differ only in their data, so they share it. */
const HeroRateStat = t0 => {
  const $ = _c(25);
  const {
    label,
    metric,
    formattedNetApy,
    hoverCardProps
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      whiteSpace: "nowrap"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label) {
    t2 = <span className="text-secondary-foreground text-xs" style={t1}>{label}</span>;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== metric) {
    t3 = isOpen => trackMetricTooltipOpenChange(isOpen, {
      page: "market",
      metric
    });
    $[3] = metric;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== hoverCardProps) {
    t4 = <RewardsHoverCard {...hoverCardProps} />;
    $[5] = hoverCardProps;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <InfoHoverCardTrigger onOpenChange={t3}>{t4}</InfoHoverCardTrigger>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <div className="gap-2xs flex items-center">{t2}{t5}</div>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      whiteSpace: "nowrap"
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== formattedNetApy) {
    t8 = <span className="text-title-sm" style={t7}>{formattedNetApy}</span>;
    $[14] = formattedNetApy;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="text-title-sm text-muted-foreground">%</span>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8) {
    t10 = <div className="flex">{t8}{t9}</div>;
    $[17] = t8;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== hoverCardProps || $[20] !== t10) {
    t11 = <IconRewardHover noHover={true} RewardsHoverCardProps={hoverCardProps} iconSize={35}>{t10}</IconRewardHover>;
    $[19] = hoverCardProps;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t6) {
    t12 = <div className="gap-sm flex flex-[1px] flex-col">{t6}{t11}</div>;
    $[22] = t11;
    $[23] = t6;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
};
export const convertToFormattedUsd = (loanAsset: HeroAsset, tokenValue: bigint | undefined | null) => {
  if (!isDefined(tokenValue)) return null;
  const token = parseApiAsset(loanAsset);
  const priceUsd = token.toUsd(tokenValue);
  const formattedPriceUsd = formatters.short.digits(2).trailingZero(false).default('-').of(priceUsd, 18);
  return splitValueAndUnit(formattedPriceUsd);
};
export default function HeroMarketSectionClient(t0) {
  const $ = _c(164);
  const {
    market,
    trustedByCurators
  } = t0;
  const {
    marketId,
    chainId,
    collateralAsset,
    loanAsset
  } = market;
  const aggregatedWarnings = aggregateMarketWarnings({
    market
  });
  let t1;
  let totalBorrowUsd;
  if ($[0] !== loanAsset || $[1] !== market.state.borrowAssets) {
    const totalBorrow = toBigInt(market.state.borrowAssets);
    totalBorrowUsd = convertToFormattedUsd(loanAsset, totalBorrow);
    t1 = formatters.short.digits(2).trailingZero(false).default("-").unit(loanAsset.symbol).of(totalBorrow, loanAsset.decimals);
    $[0] = loanAsset;
    $[1] = market.state.borrowAssets;
    $[2] = t1;
    $[3] = totalBorrowUsd;
  } else {
    t1 = $[2];
    totalBorrowUsd = $[3];
  }
  const formattedTotalBorrow = t1;
  let t2;
  if ($[4] !== market.lltv) {
    t2 = formatters.percent.digits(2).trailingZero(false).default("-").unit("%").of(toBigInt(market.lltv), 18);
    $[4] = market.lltv;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const formattedLltv = t2;
  let t3;
  if ($[6] !== collateralAsset || $[7] !== loanAsset || $[8] !== market.lltv) {
    t3 = getMarketNameParts({
      loanAsset: parseApiAsset(loanAsset),
      collateralAsset: collateralAsset ? parseApiAsset(collateralAsset) : null,
      lltv: toBigInt(market.lltv)
    });
    $[6] = collateralAsset;
    $[7] = loanAsset;
    $[8] = market.lltv;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    loanSymbol,
    collateralSymbol
  } = t3;
  let borrowApy;
  let borrowNetApy;
  let borrowRewards;
  let collateralRewards;
  let dailyBorrowApy;
  let formattedBorrowNetApy;
  let instantBorrowApy;
  let supplyApy;
  let supplyNetApy;
  let supplyRewards;
  let t4;
  let weeklyBorrowApy;
  if ($[10] !== market.rewards.borrow || $[11] !== market.rewards.collateral || $[12] !== market.rewards.supply || $[13] !== market.state.avgBorrowApy || $[14] !== market.state.avgSupplyApy || $[15] !== market.state.borrowApy || $[16] !== market.state.dailyBorrowApy || $[17] !== market.state.weeklyBorrowApy) {
    borrowRewards = normalizeMarketApyRewards(market.rewards.borrow, "borrow");
    supplyRewards = normalizeMarketApyRewards(market.rewards.supply, "supply");
    let t5;
    if ($[30] !== market.rewards.collateral) {
      t5 = toCollateralRewardEntries(market.rewards.collateral);
      $[30] = market.rewards.collateral;
      $[31] = t5;
    } else {
      t5 = $[31];
    }
    collateralRewards = t5;
    borrowApy = getParsedValue(market.state.avgBorrowApy, safeParseNumber);
    let t6;
    if ($[32] !== market.state.borrowApy) {
      t6 = getParsedValue(market.state.borrowApy, safeParseNumber);
      $[32] = market.state.borrowApy;
      $[33] = t6;
    } else {
      t6 = $[33];
    }
    instantBorrowApy = t6;
    let t7;
    if ($[34] !== market.state.dailyBorrowApy) {
      t7 = getParsedValue(market.state.dailyBorrowApy, safeParseNumber);
      $[34] = market.state.dailyBorrowApy;
      $[35] = t7;
    } else {
      t7 = $[35];
    }
    dailyBorrowApy = t7;
    let t8;
    if ($[36] !== market.state.weeklyBorrowApy) {
      t8 = getParsedValue(market.state.weeklyBorrowApy, safeParseNumber);
      $[36] = market.state.weeklyBorrowApy;
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    weeklyBorrowApy = t8;
    borrowNetApy = getNetRateOnMarket({
      rewards: borrowRewards,
      nativeRate: borrowApy
    });
    supplyApy = getParsedValue(market.state.avgSupplyApy, safeParseNumber);
    supplyNetApy = getNetRateOnMarket({
      rewards: supplyRewards,
      nativeRate: supplyApy
    });
    formattedBorrowNetApy = formatters.percent.digits(2).default("-").max(10).of(borrowNetApy, 18);
    t4 = formatters.percent.digits(2).default("-").max(10).of(supplyNetApy, 18);
    $[10] = market.rewards.borrow;
    $[11] = market.rewards.collateral;
    $[12] = market.rewards.supply;
    $[13] = market.state.avgBorrowApy;
    $[14] = market.state.avgSupplyApy;
    $[15] = market.state.borrowApy;
    $[16] = market.state.dailyBorrowApy;
    $[17] = market.state.weeklyBorrowApy;
    $[18] = borrowApy;
    $[19] = borrowNetApy;
    $[20] = borrowRewards;
    $[21] = collateralRewards;
    $[22] = dailyBorrowApy;
    $[23] = formattedBorrowNetApy;
    $[24] = instantBorrowApy;
    $[25] = supplyApy;
    $[26] = supplyNetApy;
    $[27] = supplyRewards;
    $[28] = t4;
    $[29] = weeklyBorrowApy;
  } else {
    borrowApy = $[18];
    borrowNetApy = $[19];
    borrowRewards = $[20];
    collateralRewards = $[21];
    dailyBorrowApy = $[22];
    formattedBorrowNetApy = $[23];
    instantBorrowApy = $[24];
    supplyApy = $[25];
    supplyNetApy = $[26];
    supplyRewards = $[27];
    t4 = $[28];
    weeklyBorrowApy = $[29];
  }
  const formattedSupplyNetApy = t4;
  const t5 = collateralAsset?.address;
  let t6;
  if ($[38] !== chainId || $[39] !== marketId || $[40] !== t5) {
    t6 = {
      chainId,
      marketId,
      collateralAddress: t5
    };
    $[38] = chainId;
    $[39] = marketId;
    $[40] = t5;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  const points = useMarketPoints(t6);
  const titleColor = getWarningsColor(aggregatedWarnings);
  let t7;
  if ($[42] !== chainId) {
    t7 = InterfaceChainUtils.getMetadata(chainId);
    $[42] = chainId;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  const chain = t7;
  let t8;
  if ($[44] !== loanSymbol) {
    t8 = {
      text: loanSymbol
    };
    $[44] = loanSymbol;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  const t9 = collateralAsset ? collateralSymbol : null;
  let t10;
  if ($[46] !== t9) {
    t10 = {
      text: t9,
      separator: " | "
    };
    $[46] = t9;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== formattedLltv) {
    t11 = {
      text: formattedLltv,
      separator: " "
    };
    $[48] = formattedLltv;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== t10 || $[51] !== t11 || $[52] !== t8) {
    t12 = [t8, t10, t11];
    $[50] = t10;
    $[51] = t11;
    $[52] = t8;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  const handleCopy = useSegmentedCopy(t12);
  let t13;
  if ($[54] !== loanAsset.logoURI) {
    t13 = <Avatar aria-hidden={true} src={loanAsset.logoURI} size="big" className={heroAvatarClassName} />;
    $[54] = loanAsset.logoURI;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  let t14;
  if ($[56] !== loanSymbol || $[57] !== titleColor) {
    t14 = <MarketTitle color={titleColor}>{loanSymbol}</MarketTitle>;
    $[56] = loanSymbol;
    $[57] = titleColor;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  let t15;
  if ($[59] !== collateralAsset) {
    t15 = collateralAsset && <><span aria-hidden={true} className="bg-muted-foreground h-lg max-sm:h-md w-0.5 shrink-0 rounded-full" /><span className="sr-only select-none"> backed by </span></>;
    $[59] = collateralAsset;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  let t16;
  if ($[61] !== t13 || $[62] !== t14 || $[63] !== t15) {
    t16 = <span className="gap-sm max-sm:gap-xs inline-flex items-center">{t13}{t14}{t15}</span>;
    $[61] = t13;
    $[62] = t14;
    $[63] = t15;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  let t17;
  if ($[65] !== collateralAsset || $[66] !== collateralSymbol || $[67] !== titleColor) {
    t17 = collateralAsset && <span className="gap-sm max-sm:gap-xs inline-flex items-center"><Avatar aria-hidden={true} src={collateralAsset.logoURI} size="big" className={heroAvatarClassName} /><MarketTitle color={titleColor}>{collateralSymbol}</MarketTitle></span>;
    $[65] = collateralAsset;
    $[66] = collateralSymbol;
    $[67] = titleColor;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  let t18;
  if ($[69] !== t16 || $[70] !== t17) {
    t18 = <h1 className="gap-sm max-sm:gap-xs flex flex-wrap items-center">{t16}{t17}</h1>;
    $[69] = t16;
    $[70] = t17;
    $[71] = t18;
  } else {
    t18 = $[71];
  }
  let t19;
  if ($[72] !== aggregatedWarnings) {
    t19 = <WarningIcon warnings={aggregatedWarnings} iconSize={50} />;
    $[72] = aggregatedWarnings;
    $[73] = t19;
  } else {
    t19 = $[73];
  }
  let t20;
  if ($[74] !== t18 || $[75] !== t19) {
    t20 = <div className="gap-sm flex flex-wrap items-center">{t18}{t19}</div>;
    $[74] = t18;
    $[75] = t19;
    $[76] = t20;
  } else {
    t20 = $[76];
  }
  let t21;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      lineHeight: "normal"
    };
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  let t22;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="sr-only select-none"> LLTV</span>;
    $[78] = t22;
  } else {
    t22 = $[78];
  }
  let t23;
  if ($[79] !== formattedLltv) {
    t23 = <TooltipTrigger asChild={true}><Tag variant="main"><span className="text-3xs text-foreground" style={t21}>{formattedLltv}{t22}</span></Tag></TooltipTrigger>;
    $[79] = formattedLltv;
    $[80] = t23;
  } else {
    t23 = $[80];
  }
  let t24;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <TooltipContent>Liquidation loan to value (LLTV)</TooltipContent>;
    $[81] = t24;
  } else {
    t24 = $[81];
  }
  let t25;
  if ($[82] !== t23) {
    t25 = <div className="gap-2xs flex"><Tooltip onOpenChange={_temp}>{t23}{t24}</Tooltip></div>;
    $[82] = t23;
    $[83] = t25;
  } else {
    t25 = $[83];
  }
  let t26;
  if ($[84] !== handleCopy || $[85] !== t20 || $[86] !== t25) {
    t26 = <div data-testid="market-hero-title" className="gap-xs flex flex-wrap items-center" onCopy={handleCopy}>{t20}{t25}</div>;
    $[84] = handleCopy;
    $[85] = t20;
    $[86] = t25;
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  let t27;
  if ($[88] !== marketId) {
    t27 = abbreviateAddress(marketId);
    $[88] = marketId;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  let t28;
  if ($[90] !== marketId || $[91] !== t27) {
    t28 = <ClipboardButton toCopy={marketId} toastMessage="Market ID copied to clipboard." tooltipMessage="Copy Market ID." label={t27} size="default" duration={1200} />;
    $[90] = marketId;
    $[91] = t27;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  let t29;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      cursor: "default"
    };
    $[93] = t29;
  } else {
    t29 = $[93];
  }
  let t30;
  if ($[94] !== chainId) {
    t30 = <NetworkAvatar chainId={chainId} />;
    $[94] = chainId;
    $[95] = t30;
  } else {
    t30 = $[95];
  }
  let t31;
  if ($[96] !== chain.chain.name || $[97] !== t30) {
    t31 = <Button variant="ghost" style={t29}>{t30}{chain.chain.name}</Button>;
    $[96] = chain.chain.name;
    $[97] = t30;
    $[98] = t31;
  } else {
    t31 = $[98];
  }
  let t32;
  if ($[99] !== t28 || $[100] !== t31) {
    t32 = <div className="gap-sm flex flex-wrap items-center">{t28}{t31}</div>;
    $[99] = t28;
    $[100] = t31;
    $[101] = t32;
  } else {
    t32 = $[101];
  }
  let t33;
  if ($[102] !== t26 || $[103] !== t32) {
    t33 = <div className="gap-sm flex flex-col">{t26}{t32}</div>;
    $[102] = t26;
    $[103] = t32;
    $[104] = t33;
  } else {
    t33 = $[104];
  }
  let t34;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <span className="text-secondary-foreground text-xs" style={{
      whiteSpace: "nowrap"
    }}><span>Outstanding Loans</span></span>;
    $[105] = t34;
  } else {
    t34 = $[105];
  }
  let t35;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="flex flex-wrap items-center gap-[6px]">{t34}<Toggletip onOpenChange={_temp2}><ToggletipTrigger asChild={true}><Icon role="button" tabIndex={0} aria-label="More information" className="text-secondary-foreground hover:text-foreground" icon={<Info />} /></ToggletipTrigger><ToggletipContent>The total amount currently borrowed from the market.</ToggletipContent></Toggletip></div>;
    $[106] = t35;
  } else {
    t35 = $[106];
  }
  const t36 = totalBorrowUsd ? `$${totalBorrowUsd.value}` : "-";
  let t37;
  if ($[107] !== t36) {
    t37 = <span className="text-title-sm">{t36}</span>;
    $[107] = t36;
    $[108] = t37;
  } else {
    t37 = $[108];
  }
  const t38 = totalBorrowUsd?.unit;
  let t39;
  if ($[109] !== t38) {
    t39 = <span className="text-title-sm text-muted-foreground">{t38}</span>;
    $[109] = t38;
    $[110] = t39;
  } else {
    t39 = $[110];
  }
  let t40;
  if ($[111] !== t37 || $[112] !== t39) {
    t40 = <div className="flex items-center">{t37}{t39}</div>;
    $[111] = t37;
    $[112] = t39;
    $[113] = t40;
  } else {
    t40 = $[113];
  }
  let t41;
  if ($[114] !== formattedTotalBorrow) {
    t41 = <div className="flex items-center"><span className="text-foreground text-sm">{formattedTotalBorrow}</span></div>;
    $[114] = formattedTotalBorrow;
    $[115] = t41;
  } else {
    t41 = $[115];
  }
  let t42;
  if ($[116] !== t40 || $[117] !== t41) {
    t42 = <div className="gap-sm flex flex-[1px] flex-col" data-testid="market-hero-outstanding-loans">{t35}<div className="gap-xs flex flex-col">{t40}{t41}</div></div>;
    $[116] = t40;
    $[117] = t41;
    $[118] = t42;
  } else {
    t42 = $[118];
  }
  let t43;
  if ($[119] !== market) {
    t43 = <MarketLiquidityBreakdownHoverCard market={market} />;
    $[119] = market;
    $[120] = t43;
  } else {
    t43 = $[120];
  }
  const t44 = `${chainId}:${marketId}:borrow`;
  let t45;
  if ($[121] !== collateralAsset) {
    t45 = parseApiAsset(collateralAsset);
    $[121] = collateralAsset;
    $[122] = t45;
  } else {
    t45 = $[122];
  }
  let t46;
  if ($[123] !== borrowApy || $[124] !== borrowNetApy || $[125] !== borrowRewards || $[126] !== collateralRewards || $[127] !== dailyBorrowApy || $[128] !== instantBorrowApy || $[129] !== points || $[130] !== t45 || $[131] !== weeklyBorrowApy) {
    t46 = {
      apy: borrowApy,
      rewards: borrowRewards,
      points,
      variant: "market",
      netApy: borrowNetApy,
      asset: t45,
      collateralRewards,
      instantApy: instantBorrowApy,
      dailyApy: dailyBorrowApy,
      weeklyApy: weeklyBorrowApy
    };
    $[123] = borrowApy;
    $[124] = borrowNetApy;
    $[125] = borrowRewards;
    $[126] = collateralRewards;
    $[127] = dailyBorrowApy;
    $[128] = instantBorrowApy;
    $[129] = points;
    $[130] = t45;
    $[131] = weeklyBorrowApy;
    $[132] = t46;
  } else {
    t46 = $[132];
  }
  let t47;
  if ($[133] !== formattedBorrowNetApy || $[134] !== t44 || $[135] !== t46) {
    t47 = <HeroRateStat key={t44} label="Borrow Rate (6h)" metric="rate_6h" formattedNetApy={formattedBorrowNetApy} hoverCardProps={t46} />;
    $[133] = formattedBorrowNetApy;
    $[134] = t44;
    $[135] = t46;
    $[136] = t47;
  } else {
    t47 = $[136];
  }
  const t48 = `${chainId}:${marketId}:lend`;
  let t49;
  if ($[137] !== loanAsset) {
    t49 = parseApiAsset(loanAsset);
    $[137] = loanAsset;
    $[138] = t49;
  } else {
    t49 = $[138];
  }
  let t50;
  if ($[139] !== supplyApy || $[140] !== supplyNetApy || $[141] !== supplyRewards || $[142] !== t49) {
    t50 = {
      apy: supplyApy,
      rewards: supplyRewards,
      points: NO_LEND_POINTS,
      variant: "supply",
      netApy: supplyNetApy,
      asset: t49
    };
    $[139] = supplyApy;
    $[140] = supplyNetApy;
    $[141] = supplyRewards;
    $[142] = t49;
    $[143] = t50;
  } else {
    t50 = $[143];
  }
  let t51;
  if ($[144] !== formattedSupplyNetApy || $[145] !== t48 || $[146] !== t50) {
    t51 = <HeroRateStat key={t48} label="Lend Rate (6h)" metric="lend_rate_6h" formattedNetApy={formattedSupplyNetApy} hoverCardProps={t50} />;
    $[144] = formattedSupplyNetApy;
    $[145] = t48;
    $[146] = t50;
    $[147] = t51;
  } else {
    t51 = $[147];
  }
  let t52;
  if ($[148] !== t42 || $[149] !== t43 || $[150] !== t47 || $[151] !== t51) {
    t52 = <div className="gap-lg flex w-full flex-wrap justify-start max-sm:flex-col">{t42}{t43}{t47}{t51}</div>;
    $[148] = t42;
    $[149] = t43;
    $[150] = t47;
    $[151] = t51;
    $[152] = t52;
  } else {
    t52 = $[152];
  }
  const t53 = trustedByCurators.length === 0;
  let t54;
  if ($[153] !== chainId || $[154] !== marketId || $[155] !== t53 || $[156] !== trustedByCurators) {
    t54 = <MarketTrustedBy marketId={marketId} chainId={chainId} isUnverifiedMarket={t53} curators={trustedByCurators} />;
    $[153] = chainId;
    $[154] = marketId;
    $[155] = t53;
    $[156] = trustedByCurators;
    $[157] = t54;
  } else {
    t54 = $[157];
  }
  let t55;
  if ($[158] !== t52 || $[159] !== t54) {
    t55 = <div className="gap-xl flex w-full flex-col">{t52}{t54}</div>;
    $[158] = t52;
    $[159] = t54;
    $[160] = t55;
  } else {
    t55 = $[160];
  }
  let t56;
  if ($[161] !== t33 || $[162] !== t55) {
    t56 = <div data-testid="market-hero" className="flex w-[100%] flex-col items-start gap-[48px] max-sm:gap-[60px]">{t33}{t55}</div>;
    $[161] = t33;
    $[162] = t55;
    $[163] = t56;
  } else {
    t56 = $[163];
  }
  return t56;
}
function _temp2(isOpen_0) {
  return trackMetricTooltipOpenChange(isOpen_0, {
    page: "market",
    metric: "total_borrow"
  });
}
function _temp(isOpen) {
  return trackMetricTooltipOpenChange(isOpen, {
    page: "market",
    metric: "lltv"
  });
}